/**
 * The rendered height of one pane's value-legend block, as seen from a sibling
 * inside the same chart container — what the PIP's top-left corner sits under.
 *
 * **Watched, not read once.** The legend renders nothing until the render plan
 * has rows, and the plan fills in *after* the bars arrive, so a read on mount or
 * on a bar-count change saw no block, kept 0, and the PIP sat on the legend's
 * first rows for good (2026-10-20). A `MutationObserver` on the container
 * notices the block appearing or going; a `ResizeObserver` on the block follows
 * it as plots are added, hidden or clipped. Mutations only re-find the block —
 * the height comes from the resize side, so a crosshair move that rewrites the
 * legend's values costs a `querySelector`, not a layout read.
 */

import { useLayoutEffect, useState, type RefObject } from "react";

export function useLegendHeight(
  hostRef: RefObject<HTMLElement | null>,
  paneId: string | undefined,
): number {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const host = hostRef.current?.parentElement;
    if (!host || !paneId) return setHeight(0);
    const selector = `[data-qf-legend-pane="${paneId}"]`;
    let block: HTMLElement | null = null;
    const read = () => setHeight(block ? block.offsetHeight : 0);
    const ro = new ResizeObserver(read);
    const find = () => {
      const found = host.querySelector(selector);
      const next = found instanceof HTMLElement ? found : null;
      if (next === block) return;
      if (block) ro.unobserve(block);
      block = next;
      if (block) ro.observe(block);
      read();
    };
    find();
    const mo = new MutationObserver(find);
    mo.observe(host, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      ro.disconnect();
    };
  }, [hostRef, paneId]);
  return height;
}
